import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import ProfileDropdown from "../Profile/ProfileDropdown";
import logo from "../../assets/WHL-LOGO.png";
import "./Navbar.css";
import { useCombinedContext } from "../../context/CombinedContext";
import { toast } from "react-toastify";
import axiosInstance from "../../services/auth";

const Navbar = () => {
  const location = useLocation();
  const { company, selectedCompany, setSelectedCompany, user, setUser } =
    useCombinedContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const companies = Array.isArray(company) ? company : [];

  // Set default company if not already selected
  useEffect(() => {
    if (companies.length > 0 && !selectedCompany?._id) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany, setSelectedCompany]);

  // Fetch user profile from backend if not already loaded
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axiosInstance.get("/auth/profile");
        setUser(data.data);
      } catch (error) {
        toast.error("Error fetching profile");
      } finally {
        setLoadingUser(false);
      }
    };

    if (!user) {
      fetchUser();
    } else {
      setLoadingUser(false);
    }
  }, [setUser, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Retrieve authData from storage as a fallback
  const storedAuthData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData")) ||
    {};

  // Use the user data from context if available; otherwise fallback to stored auth data
  const role = (user && user.role) || storedAuthData?.role || "user";
  const rawRoutes = (user && user.route) || storedAuthData?.route || [];

  // Normalize the routes: ensure every route string starts with "/"
  const userRoutes = rawRoutes.map((r) => (r.startsWith("/") ? r : "/" + r));

  // Masters dropdown items (reference/admin data)
  const mastersItems = [
    { path: "/company", label: "Company", icon: "bi-building" },
    { path: "/user", label: "User", icon: "bi-people" },
    { path: "/location", label: "Job Location", icon: "bi-geo-alt" },
  ];

  // HR dropdown items (people & finance)
  const hrItems = [
    { path: "/employee", label: "Employee", icon: "bi-person-badge" },
    { path: "/loan", label: "Loan", icon: "bi-cash-stack" },
    { path: "/foodallowance", label: "Food Allowance", icon: "bi-cup-hot" },
    { path: "/loan/loantable", label: "Loan Report", icon: "bi-cash-coin", accessRoute: "/loan" },
  ];

  // Invoice dropdown items (invoicing module + its report)
  const invoiceItems = [
    { path: "/report", label: role === "superadmin" ? "Invoice Report" : "Dia Inch Summary", icon: "bi-file-earmark-bar-graph", accessRoute: "/report" },
    { path: "/vessel", label: "Vessel", icon: "bi-water" },
    { path: "/invoice", label: role === "superadmin" ? "Invoice" : "Dia Inch Summary", icon: "bi-receipt" },
  ];

  // Logistics dropdown items (lists + stock/purchase reports)
  const logisticsItems = [
    { path: "/logistics/employeelist", label: "Employee", icon: "bi-person-badge", accessRoute: "/logistics" },
    { path: "/logistics/stocklist", label: "Stock List", icon: "bi-box-seam", accessRoute: "/logistics" },
    { path: "/logistics/stocktransfer", label: "Stock Transfer", icon: "bi-arrow-left-right", accessRoute: "/logistics" },
    { path: "/logistics/purchaselist", label: "Purchase List", icon: "bi-clipboard-check", accessRoute: "/logistics" },
    { path: "/logistics/stockoutlist", label: "Stock Out List", icon: "bi-box-arrow-up", accessRoute: "/logistics" },
    { path: "/stock/report", label: "Stock Report", icon: "bi-bar-chart-line", accessRoute: "/logistics" },
    { path: "/logistics/purchase-report", label: "Purchase Report", icon: "bi-clipboard-data", accessRoute: "/logistics" },
    { path: "/logistics/purchase-consolidated", label: "Purchase Consolidated", icon: "bi-file-earmark-text", accessRoute: "/logistics" },
    { path: "/logistics/stockout-consolidated", label: "Stock Out Consolidated", icon: "bi-file-earmark-spreadsheet", accessRoute: "/logistics" },
  ];

  // Check if a path is active (exact or prefix)
  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (items) => items.some((item) => location.pathname === item.path);

  // Helper: check if user has access via exact match OR parent prefix match
  const hasAccess = (itemPath, routes) => {
    return routes.some(
      (route) =>
        itemPath === route || itemPath.startsWith(route + "/"),
    );
  };

  // Filter masters items based on user routes
  const filteredMastersItems =
    role === "superadmin"
      ? mastersItems
      : mastersItems.filter((item) => userRoutes.includes(item.path));

  // Filter HR items: check exact path OR parent accessRoute
  const filteredHrItems =
    role === "superadmin"
      ? hrItems
      : hrItems.filter(
          (item) =>
            hasAccess(item.path, userRoutes) ||
            (item.accessRoute && hasAccess(item.accessRoute, userRoutes)),
        );

  // Filter invoice items: check exact path OR parent accessRoute
  const filteredInvoiceItems =
    role === "superadmin"
      ? invoiceItems
      : invoiceItems.filter(
          (item) =>
            hasAccess(item.path, userRoutes) ||
            (item.accessRoute && hasAccess(item.accessRoute, userRoutes)),
        );

  // Filter logistics items: check exact path OR parent accessRoute
  const filteredLogisticsItems =
    role === "superadmin"
      ? logisticsItems
      : logisticsItems.filter(
          (item) =>
            hasAccess(item.path, userRoutes) ||
            (item.accessRoute && hasAccess(item.accessRoute, userRoutes)),
        );



  const handleCompanyChange = (event) => {
    const selectedId = event.target.value;
    const selected = companies.find((comp) => comp._id === selectedId);
    setSelectedCompany(selected);
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);

  const handleDropdownToggle = (dropdown) => {
    setOpenDropdown((prev) => (prev === dropdown ? null : dropdown));
  };

  // Close dropdown AND mobile menu (for navigation links)
  const handleDropdownItemClick = () => {
    setOpenDropdown(null);
    closeMenu();
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 968) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <nav className="navbar mb-4">
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-logo">
          <Link to="/home" onClick={closeMenu}>
            <img src={logo} alt="Logo" className="navbar-logo-img" />
          </Link>
        </div>

        {/* Hamburger Menu Button */}
        <button className="navbar-menu-button" onClick={toggleMenu}>
          <span>
            <i className={`bi ${menuOpen ? "bi-x-lg" : "bi-list"} fs-5`}></i>
          </span>
        </button>

        {/* Pages Links */}
        <div className={`navbar-links ${menuOpen ? "open" : "closed"}`} ref={dropdownRef}>
          {/* Masters Dropdown */}
          {filteredMastersItems.length > 0 && (
            <div className={`nav-dropdown ${isDropdownActive(filteredMastersItems) ? "has-active" : ""}`}>
              <button
                className={`nav-dropdown-trigger ${isDropdownActive(filteredMastersItems) ? "active" : ""} ${openDropdown === "masters" ? "open" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle("masters");
                }}
              >
                <i className="bi bi-grid me-1"></i>
                Masters
                <i className={`bi bi-chevron-down dropdown-arrow ${openDropdown === "masters" ? "rotated" : ""}`}></i>
              </button>
              <div className={`nav-dropdown-menu ${openDropdown === "masters" ? "show" : ""}`}>
                {filteredMastersItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className={`nav-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                    onClick={handleDropdownItemClick}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* HR Dropdown */}
          {filteredHrItems.length > 0 && (
            <div className={`nav-dropdown ${isDropdownActive(filteredHrItems) ? "has-active" : ""}`}>
              <button
                className={`nav-dropdown-trigger ${isDropdownActive(filteredHrItems) ? "active" : ""} ${openDropdown === "hr" ? "open" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle("hr");
                }}
              >
                <i className="bi bi-people-fill me-1"></i>
                HR
                <i className={`bi bi-chevron-down dropdown-arrow ${openDropdown === "hr" ? "rotated" : ""}`}></i>
              </button>
              <div className={`nav-dropdown-menu ${openDropdown === "hr" ? "show" : ""}`}>
                {filteredHrItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className={`nav-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                    onClick={handleDropdownItemClick}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Invoice Dropdown */}
          {filteredInvoiceItems.length > 0 && (
            <div className={`nav-dropdown ${isDropdownActive(filteredInvoiceItems) ? "has-active" : ""}`}>
              <button
                className={`nav-dropdown-trigger ${isDropdownActive(filteredInvoiceItems) ? "active" : ""} ${openDropdown === "invoice" ? "open" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle("invoice");
                }}
              >
                <i className="bi bi-receipt me-1"></i>
                Invoice
                <i className={`bi bi-chevron-down dropdown-arrow ${openDropdown === "invoice" ? "rotated" : ""}`}></i>
              </button>
              <div className={`nav-dropdown-menu ${openDropdown === "invoice" ? "show" : ""}`}>
                {filteredInvoiceItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className={`nav-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                    onClick={handleDropdownItemClick}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Logistics Dropdown */}
          {filteredLogisticsItems.length > 0 && (
            <div className={`nav-dropdown ${isDropdownActive(filteredLogisticsItems) ? "has-active" : ""}`}>
              <button
                className={`nav-dropdown-trigger ${isDropdownActive(filteredLogisticsItems) ? "active" : ""} ${openDropdown === "logistics" ? "open" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle("logistics");
                }}
              >
                <i className="bi bi-truck me-1"></i>
                Logistics
                <i className={`bi bi-chevron-down dropdown-arrow ${openDropdown === "logistics" ? "rotated" : ""}`}></i>
              </button>
              <div className={`nav-dropdown-menu ${openDropdown === "logistics" ? "show" : ""}`}>
                {filteredLogisticsItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className={`nav-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                    onClick={handleDropdownItemClick}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Profile & Logout (Only in Mobile Menu) */}
          {menuOpen && (
            <>
              <Link to="/profile" className="navbar-link" onClick={handleDropdownItemClick}>
                <i className="bi bi-person-circle me-1"></i>
                Profile
              </Link>
              <Link to="/logout" className="navbar-link" onClick={handleDropdownItemClick}>
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </Link>
            </>
          )}
        </div>

        {/* Select Company */}
        <div className="navbar-company-selector">
          <select
            className="company-select"
            value={selectedCompany?._id || ""}
            onChange={handleCompanyChange}
          >
            {companies.map((comp) => (
              <option key={comp._id} value={comp._id}>
                {comp.company_name}
              </option>
            ))}
          </select>
        </div>

        {/* Profile Dropdown */}
        <div className={`navbar-profile-actions ${menuOpen ? "open" : ""}`}>
          {!menuOpen && (
            <div className="profile_dropdown">
              <ProfileDropdown />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
